import { describeError } from '@/lib/form/errorMessages';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Permission,
  catalogPanelAttributes,
  type CatalogItemDetails,
  type CatalogPanelAttributes,
  type CreateCatalogItemRequest,
  type EdgeListItem,
  type MaterialListItem,
  type PreviewPanelRequest,
  type UpdateCatalogItemRequest,
} from '@kreart/shared';
import { Button } from '@/components/ui/button';
import { Form, TextField, CheckboxField, FormActions, FormErrorBanner } from '@/components/form';
import { positiveIntegerFieldProps } from '@/components/form/numberInputProps';
import { useAuth } from '@/features/auth/useAuth';
import {
  useCatalogItem,
  useCreateCatalogItem,
  useDeleteCatalogItem,
  useUpdateCatalogItem,
} from '@/features/catalog/hooks';
import { errorKeyFor } from '@/features/catalog/errorMessages';
import { CatalogDangerZone } from '@/features/catalog/components/CatalogDangerZone';
import {
  EdgeAutosuggest,
  MaterialAutosuggest,
} from '@/features/project-items/shared/pickers/CatalogAutosuggest';
import { BreakdownPanel } from '@/features/pricing/components/BreakdownPanel';
import { usePreviewPanel } from '@/features/pricing/hooks';
import { useDebouncedValue } from '@/lib/useDebouncedValue';
import { useDefaultMaterialId } from '@/features/project-items/shared/useDefaultMaterial';
import { chooseEdgeForMaterial } from '@/features/project-items/shared/edgeAutoSelect';
import { edgesApi } from '@/features/catalog/components/admin/api';

const decimalRegex = /^\d+(\.\d+)?$/;
const decimalString = z.string().regex(decimalRegex);
const requiredCatalogId = z.string().min(1, 'Choose a value from the suggestions');

const formSchema = z.object({
  name: z.string().trim().min(1).max(200),
  heightMm: z.coerce.number().int().positive().max(100_000),
  widthMm: z.coerce.number().int().positive().max(100_000),
  materialId: requiredCatalogId,
  edgeId: z.string(),
  material: z.string().trim().max(100).optional(),
  edging: z.string().trim().max(100).optional(),
  cx2: z.boolean().default(false),
  unitPriceNet: decimalString,
});
type FormValues = z.infer<typeof formSchema>;

function defaults(defaultMaterialId?: string): FormValues {
  return {
    name: '',
    heightMm: 720,
    widthMm: 560,
    materialId: defaultMaterialId ?? '',
    edgeId: '',
    material: '',
    edging: '',
    cx2: false,
    unitPriceNet: '0.00',
  };
}

function parseAttributes(item: CatalogItemDetails | undefined): CatalogPanelAttributes {
  if (!item?.attributes) return {};
  const parsed = catalogPanelAttributes.safeParse(item.attributes);
  return parsed.success ? parsed.data : {};
}

function valuesFromItem(item: CatalogItemDetails): FormValues {
  const attrs = parseAttributes(item);
  return {
    ...defaults(),
    name: item.name,
    heightMm: item.defaultHeightMm ?? 720,
    widthMm: item.defaultWidthMm ?? item.defaultDepthMm ?? 560,
    materialId: attrs.materialId ?? '',
    edgeId: attrs.edgeId ?? '',
    material: attrs.material ?? item.material ?? '',
    edging: attrs.edging ?? '',
    cx2: attrs.cx2 ?? attrs.doubled ?? false,
    unitPriceNet: item.defaultCostNet ?? '0.00',
  };
}

function buildBody(
  values: FormValues,
  mode: 'create' | 'edit',
): CreateCatalogItemRequest | UpdateCatalogItemRequest {
  const attributes: CatalogPanelAttributes = {
    materialId: values.materialId || null,
    edgeId: values.edgeId || null,
    material: values.material || undefined,
    edging: values.edging || undefined,
    pricingMode: 'AUTO',
    doubled: values.cx2,
    cx2: values.cx2,
  };
  const body = {
    name: values.name,
    defaultWidthMm: values.widthMm,
    defaultHeightMm: values.heightMm,
    defaultDepthMm: null,
    thicknessMm: 18,
    material: values.material || undefined,
    defaultCostNet: values.unitPriceNet,
    currency: 'RON' as const,
    attributes: attributes as Record<string, unknown>,
  };
  return mode === 'create' ? { type: 'PANEL' as const, ...body } : body;
}

interface Props {
  mode: 'create' | 'edit';
}

export function CatalogPanelFormPage({ mode }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { id?: string };
  const id = mode === 'edit' ? params.id : undefined;
  const { user } = useAuth();
  const can = (p: string) => user?.permissions.includes(p) ?? false;
  const canUpdate = can(Permission.CATALOG_UPDATE);
  const canDelete = can(Permission.CATALOG_DELETE);
  const defaultMaterialId = useDefaultMaterialId();

  const itemQuery = useCatalogItem(id);
  const createMut = useCreateCatalogItem();
  const updateMut = useUpdateCatalogItem(id ?? '');
  const deleteMut = useDeleteCatalogItem(id ?? '');
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialListItem | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaults(defaultMaterialId),
  });

  useEffect(() => {
    if (mode !== 'edit' || !itemQuery.data) return;
    form.reset(valuesFromItem(itemQuery.data));
    setSelectedMaterial(null);
  }, [form, itemQuery.data, mode]);

  // Fill default material once it resolves (async), only for new items.
  useEffect(() => {
    if (mode !== 'create' || !defaultMaterialId) return;
    const current = form.getValues('materialId');
    if (!current) {
      form.setValue('materialId', defaultMaterialId);
    }
  }, [defaultMaterialId, form, mode]);

  // Edge auto-select
  const watchedRaw = form.watch();
  const edgeSearch = selectedMaterial?.manufacturer?.trim() ?? '';
  const edgesQuery = useQuery({
    queryKey: ['catalog-edges', 'panel-auto-select', edgeSearch],
    queryFn: () =>
      edgesApi
        .list({ page: 1, pageSize: 100, active: true, includeDeleted: false, search: edgeSearch })
        .then((res) => res.items),
    enabled:
      Boolean(selectedMaterial) &&
      selectedMaterial?.category !== 'MDF_VOPSIT' &&
      Boolean(edgeSearch),
    meta: { silent: true },
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!selectedMaterial) return;
    if (selectedMaterial.category === 'MDF_VOPSIT' || !edgeSearch) {
      form.setValue('edgeId', '', { shouldDirty: true, shouldValidate: true });
      return;
    }
    if (!edgesQuery.data) return;
    const edge = chooseEdgeForMaterial(selectedMaterial, edgesQuery.data, {
      preferLargestProduct: watchedRaw.cx2,
    });
    form.setValue('edgeId', edge?.id ?? '', { shouldDirty: true, shouldValidate: true });
    if (edge) {
      form.setValue('edging', edge.name, { shouldDirty: true });
    }
  }, [edgeSearch, edgesQuery.data, form, selectedMaterial, watchedRaw.cx2]);

  const watched = useDebouncedValue(watchedRaw, 300);
  const previewBody: PreviewPanelRequest | null = useMemo(() => {
    if (!watched.materialId || !watched.widthMm || !watched.heightMm) {
      return null;
    }
    return {
      widthMm: Number(watched.widthMm),
      heightMm: Number(watched.heightMm),
      materialId: watched.materialId,
      edgeId: watched.edgeId || null,
      doubled: watched.cx2,
      quantity: '1',
      coefficient: '1',
    };
  }, [watched]);
  const preview = usePreviewPanel(previewBody);

  useEffect(() => {
    if (preview.data) {
      const subtotal = parseFloat(preview.data.breakdown.subtotalNet);
      if (Number.isFinite(subtotal) && subtotal >= 0) {
        form.setValue('unitPriceNet', subtotal.toFixed(2), { shouldDirty: false });
      }
    }
  }, [form, preview.data]);

  const isReadOnly = mode === 'edit' && !canUpdate;
  const submitting = mode === 'create' ? createMut.isPending : updateMut.isPending;
  const submitError = mode === 'create' ? createMut.error : updateMut.error;

  const onMaterialSelected = (item: MaterialListItem | null) => {
    setSelectedMaterial(item);
    form.setValue('material', item?.name ?? '', { shouldDirty: true });
    form.setValue('edgeId', '', { shouldDirty: true, shouldValidate: true });
  };

  const onEdgeSelected = (item: EdgeListItem | null) => {
    form.setValue('edging', item?.name ?? '', { shouldDirty: true });
  };

  const onSubmit = async (values: FormValues) => {
    try {
      if (mode === 'create') {
        await createMut.mutateAsync(buildBody(values, mode) as CreateCatalogItemRequest);
        void navigate({ to: '/panels', search: {} });
        return;
      }
      if (!id) return;
      await updateMut.mutateAsync(buildBody(values, mode) as UpdateCatalogItemRequest);
    } catch {
      /* surfaced via mutation errors */
    }
  };

  const onDelete = async () => {
    if (!id) return;
    try {
      await deleteMut.mutateAsync();
      void navigate({ to: '/panels', search: {} });
    } catch {
      /* surfaced via deleteMut.error */
    }
  };

  if (mode === 'edit' && itemQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;
  }
  if (mode === 'edit' && itemQuery.isError) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        {describeError(t, itemQuery.error, errorKeyFor)}
      </div>
    );
  }

  const titleKey = mode === 'create' ? 'panels.create.title' : 'panels.edit.title';

  return (
    <div className="w-full space-y-4">
      <Link
        to="/panels"
        search={{}}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> {t('panels.backToList')}
      </Link>
      <h1 className="text-2xl font-semibold text-foreground">{t(titleKey)}</h1>

      <Form
        form={form}
        onSubmit={onSubmit}
        className="space-y-4 rounded-md border border-border bg-card p-4"
      >
        <fieldset disabled={isReadOnly} className="space-y-4">
          <TextField name="name" label={t('projectItems.panels.fields.name')} autoFocus />
          <div className="grid grid-cols-2 gap-3">
            <TextField
              name="heightMm"
              label={t('projectItems.panels.fields.heightMm')}
              {...positiveIntegerFieldProps}
            />
            <TextField
              name="widthMm"
              label={t('projectItems.panels.fields.widthMm')}
              {...positiveIntegerFieldProps}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <MaterialAutosuggest
              name="materialId"
              label={t('projectItems.panels.fields.material')}
              excludeCategories={['BLAT_TERMOREZISTENT']}
              required
              onSelectedOption={onMaterialSelected}
            />
            <EdgeAutosuggest
              name="edgeId"
              label={t('projectItems.panels.fields.edgeId')}
              emptyOptionLabel={t('pricing.noEdge')}
              onSelectedOption={onEdgeSelected}
            />
          </div>
          <div className="rounded-md border border-border p-3 space-y-3">
            <CheckboxField name="cx2" label={t('projectItems.panels.fields.cx2') ?? 'Cx2'} pill />
            <BreakdownPanel
              breakdown={preview.data?.breakdown ?? null}
              loading={preview.isFetching}
              error={preview.error}
            />
          </div>
        </fieldset>

        <FormErrorBanner error={submitError} resolveKey={errorKeyFor} />

        {!isReadOnly && (
          <FormActions
            submitLabel={t(mode === 'create' ? 'panels.create.submit' : 'common.save')}
            isSubmitting={submitting}
            submitDisabled={!form.formState.isDirty && mode === 'edit'}
          >
            <Link to="/panels" search={{}}>
              <Button type="button" variant="ghost">
                {t('common.cancel')}
              </Button>
            </Link>
          </FormActions>
        )}
      </Form>

      {mode === 'edit' && canDelete && itemQuery.data && (
        <CatalogDangerZone
          itemName={itemQuery.data.name}
          pending={deleteMut.isPending}
          error={deleteMut.error}
          onConfirm={onDelete}
        />
      )}
    </div>
  );
}
